import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {Base64SvgComponent} from '../../base64-svg/base64-svg.component';
import {heroMagnifyingGlass} from '@ng-icons/heroicons/outline';
import {PointOfInterest} from '../../../classes/point-of-interest';
import {PointOfInterestService} from '../../../shared/point-of-interest.service';
import {FormsModule} from '@angular/forms';
import {BehaviorSubject, debounceTime, Subject, Subscription} from 'rxjs';
import Fuse from 'fuse.js';
import {SearchBarService} from '../../../shared/search-bar.service';
import {NgClass} from '@angular/common';
import {Category} from '../../../classes/category';
import {Story} from '../../../classes/story';
import {GroupedByCategory} from '../../../interfaces/grouped-by-category';
import {NavigationEnd, Router} from '@angular/router';
import {heroMapPinSolid} from '@ng-icons/heroicons/solid';

@Component({
  selector: 'app-search-bar',
  imports: [
    Base64SvgComponent,
    NgIcon,
    FormsModule,
    NgClass,
  ],
  standalone: true,
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss',
  viewProviders: [provideIcons({heroMagnifyingGlass, heroMapPinSolid})]
})
export class SearchBarComponent implements OnInit, OnDestroy {
  pointOfInterests: { id: number, title: string, address: string, description: string | null, categories: Category[], isStory: boolean}[] = [];
  filteredPointOfInterests: { id: number, title: string, address: string, description: string | null, categories: Category[], isStory: boolean}[] = [];
  showDropdown = false;
  searchText?: string;
  selected: { id: number | null; isStory: boolean } = {id: null, isStory: false};
  private _searchSubject: Subject<string> = new Subject<string>();
  private _subscriptions: (BehaviorSubject<PointOfInterest[]> | Subject<string>)[] = [];
  private _dropdownCloseTimeout: ReturnType<typeof setTimeout> | undefined;
  private _isHome = false;
  private _routeSubscription: Subscription;

  constructor(private _pointOfInterestService: PointOfInterestService, private _searchBarService: SearchBarService,
              private _router: Router) {
    this._subscriptions.push(this._searchSubject);
    this._pointOfInterestService.getSearchBarItems().subscribe({
      next: (pointOfInterests: GroupedByCategory<PointOfInterest | Story>[]) => {
        this.filteredPointOfInterests = [];
        this.pointOfInterests = [];
        const searchObjects: { id: number, title: string, address: string, description: string | null, categories: Category[], isStory: boolean}[] = [];
        pointOfInterests.forEach(groupedPointOfInterestMarker => {
          groupedPointOfInterestMarker.classes.forEach(pointOfInterest => {
            const found = searchObjects.find(item => {
              if (item.id !== pointOfInterest.id) {
                return false;
              }
              if (item.address && pointOfInterest instanceof Story) {
                return;
              }
              return true;
            });
            if (found) {
              return;
            }
            searchObjects.push({
              id: pointOfInterest.id,
              title: pointOfInterest.title,
              address: pointOfInterest instanceof PointOfInterest ? pointOfInterest.address.fullAddress : '',
              description: pointOfInterest instanceof PointOfInterest ? pointOfInterest.description ?? '' : '',
              categories: pointOfInterest.categories || [],
              isStory: pointOfInterest instanceof Story,
            });
          });
        });

        this.filteredPointOfInterests = searchObjects;
        this.pointOfInterests = searchObjects;
      }
    });

    this._searchBarService.selectedPointOfInterestId$.subscribe({
      next: (selected) => {
        this.selected = selected;
        if (selected.id === null) {
          this.filteredPointOfInterests = this.pointOfInterests;
          this.searchText = '';
        }
      }
    });

    this._routeSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._isHome = event.url === '/';
      }
    });
  }

  ngOnInit() {
    this._searchSubject.pipe(debounceTime(300)).subscribe((searchValue) => {
      this._search(searchValue);
    });
  }

  focus() {
    clearTimeout(this._dropdownCloseTimeout);
    this.showDropdown = true;
  }

  blur() {
    clearTimeout(this._dropdownCloseTimeout);
    this._hideDropdown();
  }

  onSearch() {
    if (!this.searchText) {
      this.filteredPointOfInterests = this.pointOfInterests;
      return;
    }
    this._searchSubject.next(this.searchText);
  }

  private _search(searchValue: string) {
    const fuse = new Fuse(this.pointOfInterests, {
      includeScore: true,
      threshold: 0.4,
      keys: ['title', 'address', 'description', 'categories.name'],
    });

    this.filteredPointOfInterests = fuse.search(searchValue).map(result => {
      return {
        id: result.item.id,
        title: result.item.title,
        address: result.item.address,
        description: result.item.description,
        categories: result.item.categories,
        isStory: result.item.isStory,
      };
    });
  }

  ngOnDestroy() {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
    this._routeSubscription?.unsubscribe();
  }

  setSelectedPointOfInterest(selected: {
    id: number;
    title: string;
    address: string;
    description: string | null;
    categories: Category[],
    isStory: boolean,
  }) {
    if (this._isHome) {
      this._router.navigateByUrl('/kaart').then(() => {
        setTimeout(() => {
          this._selectedPointOfInterestAction(selected);
        }, 500);
      });
    } else {
      this._selectedPointOfInterestAction(selected);
    }
  }

  private _selectedPointOfInterestAction(selected: {
    id: number;
    title: string;
    address: string;
    description: string | null;
    categories: Category[],
    isStory: boolean,
  }) {
    this._searchBarService.setSelectedPointOfInterestId(selected.id, selected.isStory);
    this.searchText = this.filteredPointOfInterests.find(item => item.id === selected.id)?.title;
    clearTimeout(this._dropdownCloseTimeout);
    this._hideDropdown();
  }

  private _hideDropdown() {
    this._dropdownCloseTimeout = setTimeout(() => {
      this.showDropdown = false;
    }, 200);
  }

  keyDown() {
    // todo: implement enter to submit
  }
}
